import { motion } from "framer-motion";
import { Crosshair, PauseCircle, XCircle, RotateCcw, Loader2, ExternalLink } from "lucide-react";
import { STATUS_LABELS } from "./useMarketMap";

const CHANNEL = { ai_knowledge: "AI knowledge", web_lists: "Web list", lookalike: "Lookalike" };
const TONE = { untouched: "text-ism-teal bg-ism-teal/10 border-ism-teal/30", hunted: "text-blue-300 bg-blue-500/10 border-blue-500/30",
  parked: "text-amber-300 bg-amber-500/10 border-amber-500/30", not_fit: "text-gray-400 bg-white/5 border-white/10", unverified: "text-gray-500 bg-white/5 border-white/10", pending: "text-gray-400 bg-white/5 border-dashed border-white/20" };

const FitBar = ({ score }) => (
  <div className="flex items-center gap-2 min-w-[88px]">
    <div className="h-1.5 w-14 rounded-full bg-white/5 overflow-hidden"><div className={`h-full ${score >= 80 ? "bg-ism-teal" : score >= 60 ? "bg-amber-400" : "bg-gray-500"}`} style={{ width: `${score}%` }} /></div>
    <span className="text-xs tabular-nums text-gray-300">{score}%</span>
  </div>
);

const Action = ({ icon: Icon, label, onClick, tone = "text-gray-400 hover:text-white", testId, busy }) => (
  <button onClick={onClick} title={label} aria-label={label} disabled={busy} data-testid={testId}
    className={`p-1.5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50 ${tone}`}>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
  </button>
);

export const MarketRow = ({ firm, i, onStatus, onHunt, hunting }) => (
  <motion.tr initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.025 }}
    className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`market-row-${firm.firmId}`}>
    <td className="py-3 pr-3 align-top">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-white text-sm font-medium truncate" data-testid="market-row-name">{firm.companyName}</span>
        {firm.companyDomain && <a href={`https://${firm.companyDomain}`} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-ism-teal flex-shrink-0" data-testid="market-row-site"><ExternalLink className="h-3 w-3" /></a>}
      </div>
      <div className="text-xs text-gray-500 truncate">{firm.why?.[0] || firm.description || "—"}</div>
      <div className="sm:hidden text-[11px] text-gray-400 mt-0.5 leading-snug" data-testid="market-row-mobile-meta">
        {firm.employeeCount ? `${firm.employeeCount.toLocaleString()} emp` : "size unknown"} · {firm.fitScore || 0}% fit{firm.sizeFit === "tolerance" ? " · 1 bracket out" : ""}
      </div>
    </td>
    <td className="py-3 pr-3 text-sm text-gray-300 tabular-nums whitespace-nowrap hidden sm:table-cell">
      {firm.employeeCount ? firm.employeeCount.toLocaleString() : "—"}
      {firm.sizeFit === "tolerance" && <span className="ml-1.5 px-1 py-px text-[10px] rounded border border-amber-400/30 text-amber-300/90" title="One size bracket outside your range (allowed by your tolerance)" data-testid="market-row-tolerance">1 bracket out</span>}
    </td>
    <td className="py-3 pr-3 text-sm text-gray-400 hidden md:table-cell max-w-[160px] truncate">{(firm.companyIndustry || "—").replace(/\b\w/g, (c) => c.toUpperCase())}</td>
    <td className="py-3 pr-3 text-sm text-gray-400 hidden lg:table-cell max-w-[160px] truncate">{firm.hq || "—"}</td>
    <td className="py-3 pr-3 hidden sm:table-cell"><FitBar score={firm.fitScore || 0} /></td>
    <td className="py-3 pr-3 hidden xl:table-cell">
      <div className="flex flex-wrap gap-1">{(firm.channels || []).map((c) => <span key={c} className="px-1.5 py-0.5 text-[10px] rounded border border-white/10 text-gray-400">{CHANNEL[c] || c}</span>)}</div>
    </td>
    <td className="py-3 pr-3"><span className={`px-2 py-0.5 text-[11px] rounded-full border whitespace-nowrap ${TONE[firm.status]}`} data-testid="market-row-status">{STATUS_LABELS[firm.status]}</span></td>
    <td className="py-3 text-right whitespace-nowrap">
      {firm.status === "untouched" && firm.verified && <Action icon={Crosshair} label="Hunt here" tone="text-ism-teal hover:text-ism-teal" onClick={() => onHunt(firm)} busy={hunting === firm.firmId} testId="market-row-hunt" />}
      {firm.status !== "parked" && firm.status !== "hunted" && <Action icon={PauseCircle} label="Park for later" onClick={() => onStatus(firm, "parked")} testId="market-row-park" />}
      {firm.status !== "not_fit" && firm.status !== "hunted" && <Action icon={XCircle} label="Not a fit" tone="text-gray-400 hover:text-red-400" onClick={() => onStatus(firm, "not_fit")} testId="market-row-notfit" />}
      {(firm.status === "parked" || firm.status === "not_fit") && <Action icon={RotateCcw} label={firm.verified ? "Back to untouched" : "Back to unverified"} onClick={() => onStatus(firm, "untouched")} testId="market-row-restore" />}
    </td>
  </motion.tr>
);

export const MarketTable = ({ firms, loading, onStatus, onHunt, hunting, empty, hasMore, loadingMore, onLoadMore }) => {
  if (loading) return <div className="flex justify-center py-20" data-testid="market-loading"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-ism-teal" /></div>;
  if (!firms.length) return (
    <div className="glass-card p-10 text-center" data-testid="market-empty">
      <p className="text-gray-300">{empty ? "Your market hasn't been mapped yet." : "Nothing here with the current filter."}</p>
      <p className="text-sm text-gray-500 mt-1">{empty ? "Press “Map my market” and the agent will build a verified list of firms that fit your ICP." : "Try another status tab or clear the search."}</p>
    </div>
  );
  return (
    <div className="glass-card overflow-x-auto" data-testid="market-table">
      <table className="w-full text-left table-fixed">
        <colgroup>
          <col />
          <col className="hidden sm:table-column w-[120px]" />
          <col className="hidden md:table-column w-[160px]" />
          <col className="hidden lg:table-column w-[150px]" />
          <col className="hidden sm:table-column w-[110px]" />
          <col className="hidden xl:table-column w-[170px]" />
          <col className="w-[84px] sm:w-[112px]" />
          <col className="w-[100px] sm:w-[128px]" />
        </colgroup>
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-gray-500">
            <th className="py-3 px-4 pr-3 font-medium">Company</th>
            <th className="py-3 pr-3 font-medium hidden sm:table-cell">Employees</th>
            <th className="py-3 pr-3 font-medium hidden md:table-cell">Industry</th>
            <th className="py-3 pr-3 font-medium hidden lg:table-cell">HQ</th>
            <th className="py-3 pr-3 font-medium hidden sm:table-cell">Fit</th>
            <th className="py-3 pr-3 font-medium hidden xl:table-cell">Found via</th>
            <th className="py-3 pr-3 font-medium">Status</th>
            <th className="py-3 pr-4 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="[&>tr>td:first-child]:pl-4 [&>tr>td:last-child]:pr-4">
          {firms.map((f, i) => <MarketRow key={f.firmId} firm={f} i={i} onStatus={onStatus} onHunt={onHunt} hunting={hunting} />)}
        </tbody>
      </table>
      {hasMore && (
        <div className="p-3 border-t border-white/5 text-center">
          <button onClick={onLoadMore} disabled={loadingMore} data-testid="market-load-more" className="text-xs text-ism-teal hover:underline disabled:opacity-50">
            {loadingMore ? "Loading…" : "Load more firms"}
          </button>
        </div>
      )}
    </div>
  );
};
